// Pure UX helpers for the seller newsletter screen. Everything here is advisory: the server
// re-resolves the audience and quota at send time and its answer always wins (plan decision 14).

export const SUBJECT_MAX = 200;
export const BODY_MAX = 20000;

// The server accepts any future time within 30 days (plan decision 15). The screen asks for a
// little more lead, since a send inside the last minute before it starts cannot be cancelled, and
// stops a minute short of the limit so a slow click does not land past it.
export const SCHEDULE_MIN_LEAD_MS = 5 * 60 * 1000;
export const SCHEDULE_MAX_AHEAD_MS = 30 * 24 * 60 * 60 * 1000 - 60 * 1000;

const TIER_LABELS = {
    free: 'Free (trial or lapsed plan)',
    basic: 'Basic',
    standard: 'Standard',
    advanced: 'Advanced',
};

export function tierLabel(tier) {
    return TIER_LABELS[tier] || 'your current plan';
}

export function formatDate(value) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
    });
}

export function formatDateTime(value) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, {dateStyle: 'medium', timeStyle: 'short'});
}

const pad = value => String(value).padStart(2, '0');

/** A Date as the `YYYY-MM-DDTHH:mm` string a `datetime-local` input uses, in local time. */
export function toLocalInputValue(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
        + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The earliest and latest time the schedule input offers, as Dates and as input strings. */
export function scheduleBounds(now = new Date()) {
    const min = new Date(now.getTime() + SCHEDULE_MIN_LEAD_MS);
    const max = new Date(now.getTime() + SCHEDULE_MAX_AHEAD_MS);
    return {min, max, minValue: toLocalInputValue(min), maxValue: toLocalInputValue(max)};
}

/** The seller's timezone, for the line under the schedule input. */
export function timezoneLabel() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (err) {
        return '';
    }
}

/**
 * Turn a `datetime-local` value into the ISO timestamp the API takes, or say why it cannot be
 * sent. Advisory only: the server checks the same window against its own clock.
 *
 * @returns {{ok: true, scheduledAt: string, date: Date} | {ok: false, message: string}}
 */
export function parseSchedule(value, now = new Date()) {
    if (!value) return {ok: false, message: 'Pick a date and time.'};
    // A date-time without an offset is read in the browser's local timezone.
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return {ok: false, message: 'Pick a valid date and time.'};
    const {min, max} = scheduleBounds(now);
    if (date < min) {
        return {ok: false, message: 'Pick a time at least 5 minutes from now.'};
    }
    if (date > max) {
        return {ok: false, message: 'Pick a time within the next 30 days.'};
    }
    return {ok: true, scheduledAt: date.toISOString(), date};
}

/** "October 2026", for the calendar month (UTC) a send's recipients count against. */
export function quotaMonthLabel(value) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, {year: 'numeric', month: 'long', timeZone: 'UTC'});
}

/** Whether a send at `scheduledAt` counts against the month `quota` describes. */
export function countsAgainstCurrentMonth(scheduledAt, quota) {
    if (!quota?.resetsAt) return true;
    return new Date(scheduledAt).getTime() < new Date(quota.resetsAt).getTime();
}

function plural(count, one, many) {
    return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

function quotaSentence(quota, recipientCount) {
    const resets = formatDate(quota.resetsAt);
    return `${tierLabel(quota.tier)} allows ${plural(quota.cap, 'recipient email', 'recipient emails')}`
        + ` per month. You have used ${quota.used.toLocaleString()} and have`
        + ` ${quota.remaining.toLocaleString()} left; your audience is`
        + ` ${plural(recipientCount, 'subscriber', 'subscribers')}.`
        + (resets ? ` Your allowance resets on ${resets}.` : '');
}

/**
 * Why a send cannot go out right now, or null when it can. Mirrors the server's three refusals so
 * the screen explains the state before the seller tries.
 *
 * @returns {null | {kind: 'plan-required'|'empty-audience'|'quota-exceeded', message: string}}
 */
export function describeSendBlock({recipientCount, quota}) {
    if (!quota) return null;
    const capacity = sendCapacity(quota);
    if (quota.cap === 0 && capacity === 0) {
        return {
            kind: 'plan-required',
            message: quota.totalRemaining !== undefined
                ? 'Your plan has no monthly allowance. Add extra sends to email your subscribers.'
                : 'Sending newsletters needs an active paid plan. You can collect subscribers'
                    + ' now, but your current plan cannot send.',
        };
    }
    if (recipientCount === 0) {
        return {
            kind: 'empty-audience',
            message: 'You have no confirmed subscribers yet.',
        };
    }
    if (recipientCount > capacity) {
        return {
            kind: 'quota-exceeded',
            message: `This would send ${plural(recipientCount, 'email', 'emails')}, but ${quotaSentence(quota, recipientCount)}`
                + (quota.totalRemaining !== undefined
                    ? ` You have ${capacity.toLocaleString()} recipient emails available in total.`
                        + ' Add extra sends or wait for your monthly reset. The whole audience must fit.'
                    : ' A newsletter is never sent to part of your audience, so upgrade your plan'
                        + ' or wait for the reset.'),
        };
    }
    return null;
}

export function sendCapacity(quota) {
    return quota?.totalRemaining ?? quota?.remaining ?? 0;
}

const VALIDATION_COPY = {
    'invalid-subject': 'The subject is empty or longer than 200 characters.',
    'invalid-body': 'The message is empty, longer than 20,000 characters, or too large once'
        + ' formatted.',
    'invalid-locale': 'That footer language is not supported.',
    'invalid-scheduled-at': 'That send time is no longer valid. Pick a time in the future, within'
        + ' the next 30 days.',
    'invalid-idempotency-key': 'Something went wrong preparing this send. Please reload and try again.',
};

/** Seller-facing sentence for a failed send. Never a bare status code. */
export function describeRefusal(error) {
    const quota = error?.data?.quota;
    const recipientCount = error?.data?.recipientCount;
    switch (error?.reason) {
        case 'newsletter-plan-required':
            return describeSendBlock({
                recipientCount: recipientCount ?? 0,
                quota: quota || {cap: 0},
            })?.message || 'Your plan cannot send newsletters.';
        case 'newsletter-empty-audience':
            return 'Nothing was sent: you have no confirmed subscribers.';
        case 'newsletter-quota-exceeded':
            return quota
                ? `Nothing was sent. ${describeSendBlock({recipientCount, quota})?.message || ''}`.trim()
                : 'Nothing was sent: this is more than your monthly allowance.';
        case 'newsletter-credit-review-required':
            return 'Your newsletter credits need review. Please contact support before retrying.';
        default:
            if (VALIDATION_COPY[error?.reason]) return VALIDATION_COPY[error.reason];
            return 'Something went wrong and nothing was confirmed as sent. Please try again.';
    }
}

/** Seller-facing sentence for a cancel the server refused. */
export function describeCancelRefusal(error) {
    if (error?.reason === 'newsletter-send-started') {
        return 'This newsletter has already started sending, so it can no longer be cancelled.';
    }
    return 'Something went wrong and the newsletter was not cancelled. Please try again.';
}

const STATUS_LABELS = {
    scheduled: 'Scheduled',
    sending: 'Sending',
    complete: 'Sent',
};

export function statusLabel(status) {
    return STATUS_LABELS[status] || status || '';
}

export function newIdempotencyKey() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `nl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
